import React, { useState, useEffect } from 'react';
import { 
  Box, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Avatar,
  Typography,
  Divider,
  IconButton,
  Drawer,
  useTheme,
  useMediaQuery,
  FormControl,
  Select,
  MenuItem,
  Button,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  MenuBook as MenuBookIcon,
  Group as GroupIcon,
  Chat as ChatIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  CameraAlt as CameraAltIcon,
  Storage as StorageIcon,
  Assessment as AssessmentIcon,
  BarChart as BarChartIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  GitHub as GitHubIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAvatar } from '../contexts/AvatarContext';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config';
import axios from 'axios';
const DRAWER_WIDTH = 250;
const REALTIME_URL = process.env.REACT_APP_REALTIME_URL || 'http://localhost:3123/';

const Sidebar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { avatarSettings } = useAvatar();
  const { user, logout } = useAuth();
  const defaultCategory = '一般';
  
  const [selectedCategory, setSelectedCategory] = useState(() => {
    // Get from localStorage or use default
    return localStorage.getItem('selectedCategory') || defaultCategory;
  });

  const [githubInfo, setGithubInfo] = useState({
    id: 'voicetube/fubon',
    buildDate: process.env.REACT_APP_BUILD_DATE || new Date().toLocaleDateString(),
    lastUpdated: null,
    branch: null,
    loading: true
  });

  useEffect(() => {
    // Get category from URL or use default
    const searchParams = new URLSearchParams(location.search);
    const categoryFromUrl = searchParams.get('category');
    
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
      localStorage.setItem('selectedCategory', categoryFromUrl);
    } else {
      // Set initial URL parameter if not present
      searchParams.set('category', defaultCategory);
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    }
  }, [location.search]);

  useEffect(() => {
    const fetchLastCommit = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/github-last-update`);
        if (response.status === 200) {
          setGithubInfo(prev => ({
            ...prev,
            lastUpdated: new Date(response.data.lastUpdated).toLocaleString(),
            branch: response.data.branch,
            loading: false
          }));
        } else {
          throw new Error('Failed to fetch last update');
        }
      } catch (error) {
        console.error('Error fetching GitHub data:', error);
        setGithubInfo(prev => ({
          ...prev,
          lastUpdated: 'Unknown',
          branch: 'Unknown',
          loading: false
        }));
      }
    };

    fetchLastCommit();
  }, []);

  const categories = [
    { value: '商業', label: '商業' },
    { value: '一般', label: '一般' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCategoryChange = (event) => {
    const newCategory = event.target.value;
    setSelectedCategory(newCategory);
    localStorage.setItem('selectedCategory', newCategory);
    
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('category', newCategory);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Helper function to create URL with category
  const getUrlWithCategory = (path) => {
    const searchParams = new URLSearchParams(location.search);
    const category = searchParams.get('category') || selectedCategory || defaultCategory;
    return `${path}?category=${category}`;
  };

  const drawerContent = (
    <>
      {/* Profile Section */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          sx={{ width: 40, height: 40 }}
          alt={user ? user.username : 'Guest'}
          src={user ? `https://api.dicebear.com/7.x/${avatarSettings.style}/svg?seed=${user.username}&backgroundColor=${avatarSettings.bgColor}` : undefined}
        />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1">
            {user ? user.username : '訪客'}
          </Typography>
          {user && (
            <FormControl size="small" fullWidth>
              <Select
                value={selectedCategory}
                onChange={handleCategoryChange}
                sx={{ minWidth: 120 }}
              >
                {categories.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      <Divider />

      {/* Navigation Menu */}
      <List>
        <ListItem 
          button 
          component={Link} 
          to={getUrlWithCategory('/scenarios')}
          selected={location.pathname === '/scenarios'}
          sx={{
            '&.Mui-selected': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              '& .MuiListItemIcon-root': {
                color: 'primary.main',
              },
            },
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            }
          }}
        >
          <ListItemIcon>
            <MenuBookIcon />
          </ListItemIcon>
          <ListItemText primary="陪練課程" />
        </ListItem>

        <ListItem 
          button 
          component="a"
          href={selectedCategory === '一般' ? `${REALTIME_URL}?agentConfig=fubon_outbound` : REALTIME_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            }
          }}
        >
          <ListItemIcon>
            <GroupIcon />
          </ListItemIcon>
          <ListItemText primary="實戰演練" />
        </ListItem>

        <ListItem 
          button 
          component={Link} 
          to="/history"
          selected={location.pathname === '/history'}
          sx={{
            '&.Mui-selected': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              '& .MuiListItemIcon-root': {
                color: 'primary.main',
              },
            },
            '&:hover': {
               backgroundColor: 'rgba(0, 0, 0, 0.08)',
             }
          }}
        >
          <ListItemIcon>
            <ChatIcon />
          </ListItemIcon>
          <ListItemText primary="練習紀錄" />
        </ListItem>

        <ListItem
          button
          component={Link}
          to="/evaluations"
          selected={location.pathname === '/evaluations'}
          sx={{
            '&.Mui-selected': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              '& .MuiListItemIcon-root': {
                color: 'primary.main',
              },
            },
            '&:hover': {
               backgroundColor: 'rgba(0, 0, 0, 0.08)',
             }
          }}
        >
          <ListItemIcon>
            <PlaylistAddCheckIcon />
          </ListItemIcon>
          <ListItemText primary="評估結果列表" />
        </ListItem>

        <ListItem 
          button 
          component={Link} 
          to="/feedback-results"
          selected={location.pathname === '/feedback-results'}
          sx={{
            '&.Mui-selected': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              '& .MuiListItemIcon-root': {
                color: 'primary.main',
              },
            }
          }}
        >
          <ListItemIcon>
            <BarChartIcon />
          </ListItemIcon>
          <ListItemText primary="用戶回饋列表" />
        </ListItem>

        <ListItem 
          button 
          component={Link} 
          to="/settings"
          selected={location.pathname === '/settings'}
          sx={{
            '&.Mui-selected': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              '& .MuiListItemIcon-root': {
                color: 'primary.main',
              },
            }
          }}
        >
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText primary="設定" />
        </ListItem>

      </List>

      <Divider />

      {/* Auth Section */}
      <List>
        {user ? (
          <ListItem button onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="登出" />
          </ListItem>
        ) : (
          <ListItem button component={Link} to="/login">
            <ListItemIcon>
              <LoginIcon />
            </ListItemIcon>
            <ListItemText primary="登入" />
          </ListItem>
        )}
      </List>

      <Divider />
      
      {/* GitHub Info */}
      <Box sx={{ p: 2, mt: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Last updated ({githubInfo.branch || 'main'}): {githubInfo.loading ? 
              <CircularProgress size={10} sx={{ ml: 1 }} /> : 
              githubInfo.lastUpdated || 'Unknown'}
          </Typography>
        </Box>
      </Box>
    </>
  );

  return (
    <>
      {/* Mobile hamburger menu */}
      {isMobile && (
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ 
            position: 'fixed',
            left: 16,
            top: 16,
            zIndex: 1200,
            backgroundColor: 'white',
            boxShadow: 1,
            '&:hover': {
              backgroundColor: '#f5f5f5',
            }
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: DRAWER_WIDTH,
            backgroundColor: 'white',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop permanent drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH,
            backgroundColor: 'white',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar; 